import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { offerApi, candidateApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';

const statusColors = {
  PENDING: '#f59e0b', SENT: '#3b82f6', VIEWED: '#8b5cf6',
  SIGNED: '#10b981', REVOKED: '#ef4444', EXPIRED: '#6b7280',
};

const s = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 24 },
  form: { background: '#fff', borderRadius: 12, padding: 32, maxWidth: 720, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 32 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 },
  input: (err) => ({ width: '100%', padding: '10px 14px', border: `1px solid ${err ? '#ef4444' : '#ddd'}`, borderRadius: 6, fontSize: 14, marginBottom: 4, boxSizing: 'border-box' }),
  select: { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, marginBottom: 16, background: '#fff', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, marginBottom: 16, minHeight: 80, resize: 'vertical', boxSizing: 'border-box' },
  errorText: { color: '#ef4444', fontSize: 12, marginBottom: 12, display: 'block' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 13 },
  btn: { padding: '12px 28px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  result: { marginTop: 32, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 24 },
  qrWrap: { display: 'flex', justifyContent: 'center', margin: '16px 0' },
  tokenBox: { background: '#1a1a2e', color: '#7c83fd', padding: '10px 16px', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 12, padding: 0, width: 720, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
  cancelBtn: { padding: '10px 24px', background: '#fff', color: '#666', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, cursor: 'pointer' },
  section: { background: '#f8f9fa', borderRadius: 8, padding: '16px 20px', marginBottom: 16 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#7c83fd', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 },
  fieldRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' },
  fieldInput: { flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  fieldLabel: { width: 140, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' },
  removeBtn: { padding: '6px 10px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  addBtn: { padding: '8px 16px', background: '#f0f4ff', color: '#7c83fd', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  templateBtn: (active) => ({ padding: '8px 16px', background: active ? '#7c83fd' : '#fff', color: active ? '#fff' : '#555', border: `1px solid ${active ? '#7c83fd' : '#ddd'}`, borderRadius: 20, fontSize: 12, cursor: 'pointer', fontWeight: 600 }),
};

// ── Default templates ────────────────────────────────────────────────────────
const TEMPLATES = {
  standard: {
    name: 'Standard Offer',
    fields: [
      { key: 'position', label: 'Position', value: '' },
      { key: 'employmentType', label: 'Employment Type', value: 'Full-time' },
      { key: 'salary', label: 'Annual CTC', value: '' },
      { key: 'startDate', label: 'Start Date', value: '' },
      { key: 'location', label: 'Location', value: '' },
    ],
    letterBody: '',
    closingText: '',
  },
  detailed: {
    name: 'Detailed Offer',
    fields: [
      { key: 'position', label: 'Position', value: '' },
      { key: 'employmentType', label: 'Employment Type', value: 'Full-time' },
      { key: 'department', label: 'Department', value: '' },
      { key: 'reportingTo', label: 'Reporting To', value: '' },
      { key: 'salary', label: 'Annual CTC', value: '' },
      { key: 'bonus', label: 'Joining Bonus', value: '' },
      { key: 'startDate', label: 'Start Date', value: '' },
      { key: 'location', label: 'Location', value: '' },
      { key: 'probation', label: 'Probation Period', value: '6 months' },
      { key: 'benefits', label: 'Benefits', value: 'Health Insurance, PF, Gratuity' },
    ],
    letterBody: '',
    closingText: '',
  },
  internship: {
    name: 'Internship',
    fields: [
      { key: 'position', label: 'Position', value: '' },
      { key: 'employmentType', label: 'Type', value: 'Internship' },
      { key: 'stipend', label: 'Monthly Stipend', value: '' },
      { key: 'duration', label: 'Duration', value: '6 months' },
      { key: 'startDate', label: 'Start Date', value: '' },
      { key: 'location', label: 'Location', value: '' },
    ],
    letterBody: '',
    closingText: '',
  },
  custom: {
    name: 'Custom',
    fields: [],
    letterBody: '',
    closingText: '',
  },
};

// ── Saved templates from localStorage ────────────────────────────────────────
const SAVED_KEY = 'secureoffer_templates';
const loadSavedTemplates = () => {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) || '{}'); } catch { return {}; }
};
const saveTemplate = (name, template) => {
  const saved = loadSavedTemplates();
  saved[name] = template;
  localStorage.setItem(SAVED_KEY, JSON.stringify(saved));
};

export default function CreateOfferPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedCandidate = searchParams.get('candidateId') || '';
  const tenantName = user?.tenantName || user?.tenantCode || 'Your Company';

  // Form state
  const [candidateId, setCandidateId] = useState(preselectedCandidate);
  const [title, setTitle] = useState('');
  const [expiryHours, setExpiryHours] = useState(72);
  const [activeTemplate, setActiveTemplate] = useState('standard');
  const [fields, setFields] = useState([...TEMPLATES.standard.fields]);
  const [letterBody, setLetterBody] = useState('');
  const [closingText, setClosingText] = useState('');
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [result, setResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState(loadSavedTemplates());
  const [saveTemplateName, setSaveTemplateName] = useState('');

  const { data: candidates = [] } = useQuery({
    queryKey: ['candidates'],
    queryFn: () => candidateApi.list().then(r => r.data),
  });

  const selectedCandidate = candidates.find(c => c.id === candidateId);

  // Switch template
  const switchTemplate = (key) => {
    const tmpl = key.startsWith('saved:')
      ? savedTemplates[key.replace('saved:', '')]
      : TEMPLATES[key];
    if (!tmpl) return;
    setActiveTemplate(key);
    setFields(tmpl.fields.map(f => ({ ...f })));
    setLetterBody(tmpl.letterBody || '');
    setClosingText(tmpl.closingText || '');
  };

  // Field management
  const updateField = (idx, prop, val) => {
    const updated = [...fields];
    updated[idx] = { ...updated[idx], [prop]: val };
    setFields(updated);
  };
  const removeField = (idx) => setFields(fields.filter((_, i) => i !== idx));
  const addField = () => setFields([...fields, { key: '', label: '', value: '' }]);

  // Build JSON from fields
  const buildOfferDetails = () => {
    const obj = {};
    fields.forEach(f => {
      if (f.key.trim()) obj[f.key.trim()] = f.value;
    });
    if (letterBody.trim()) obj.letterBody = letterBody.trim();
    if (closingText.trim()) obj.closingText = closingText.trim();
    return JSON.stringify(obj);
  };

  // Save current as template
  const handleSaveTemplate = () => {
    if (!saveTemplateName.trim()) { toast.error('Enter a template name'); return; }
    saveTemplate(saveTemplateName.trim(), { name: saveTemplateName.trim(), fields: [...fields], letterBody, closingText });
    setSavedTemplates(loadSavedTemplates());
    setSaveTemplateName('');
    toast.success(`Template "${saveTemplateName.trim()}" saved`);
  };

  // Validate
  const validate = () => {
    const e = {};
    if (!candidateId) e.candidateId = 'Select a candidate';
    if (!title.trim()) e.title = 'Offer title is required';
    if (fields.length === 0) e.fields = 'Add at least one field';
    if (fields.some(f => !f.key.trim())) e.fields = 'All fields need a key name';
    return e;
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => offerApi.create(data),
    onSuccess: (res) => {
      setResult(res.data);
      setServerError('');
      qc.invalidateQueries(['offers']);
      toast.success('Offer created');
    },
    onError: (err) => setServerError(err.response?.data?.message || 'Failed to create offer'),
  });

  const notifyMutation = useMutation({
    mutationFn: (offerId) => offerApi.notify(offerId),
    onSuccess: () => toast.success('Email sent to candidate'),
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send email'),
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    setServerError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    createMutation.mutate({
      candidateId,
      title,
      offerDetails: buildOfferDetails(),
      expiryHours: parseInt(expiryHours),
    });
  };

  const handlePreview = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setShowPreview(true);
  };

  return (
    <div>
      <h1 style={s.title}>Create Offer</h1>

      <div style={s.form}>
        {serverError && <div style={s.errorBox}>⚠️ {serverError}</div>}

        {/* ── Candidate & Title ── */}
        <label style={s.label}>Candidate</label>
        {candidates.length > 0 ? (
          <select style={s.select} value={candidateId} onChange={e => { setCandidateId(e.target.value); setErrors(p => ({ ...p, candidateId: '' })); }}>
            <option value="">— Select a candidate —</option>
            {candidates.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
          </select>
        ) : (
          <input style={s.input(!!errors.candidateId)} placeholder="No candidates — add one first" value={candidateId} onChange={e => setCandidateId(e.target.value)} />
        )}
        {errors.candidateId && <span style={s.errorText}>{errors.candidateId}</span>}

        <label style={s.label}>Offer Title</label>
        <input style={s.input(!!errors.title)} placeholder="e.g. Software Engineer - Full Time"
          value={title} onChange={e => { setTitle(e.target.value); setErrors(p => ({ ...p, title: '' })); }} />
        {errors.title && <span style={s.errorText}>{errors.title}</span>}

        {/* ── Template Selector ── */}
        <div style={{ ...s.section, marginTop: 16 }}>
          <div style={s.sectionTitle}>📋 Template</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(TEMPLATES).map(([key, tmpl]) => (
              <button key={key} style={s.templateBtn(activeTemplate === key)} onClick={() => switchTemplate(key)}>
                {tmpl.name}
              </button>
            ))}
            {Object.keys(savedTemplates).map(name => (
              <button key={name} style={s.templateBtn(activeTemplate === `saved:${name}`)} onClick={() => switchTemplate(`saved:${name}`)}>
                💾 {name}
              </button>
            ))}
          </div>
        </div>

        {/* ── Offer Fields ── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>📝 Offer Details</div>
          {errors.fields && <span style={s.errorText}>{errors.fields}</span>}

          {/* Header row */}
          <div style={{ ...s.fieldRow, marginBottom: 4 }}>
            <span style={{ width: 140, fontSize: 11, color: '#999', fontWeight: 600 }}>FIELD NAME</span>
            <span style={{ flex: 1, fontSize: 11, color: '#999', fontWeight: 600 }}>LABEL (display)</span>
            <span style={{ flex: 1, fontSize: 11, color: '#999', fontWeight: 600 }}>VALUE</span>
            <span style={{ width: 36 }}></span>
          </div>

          {fields.map((f, i) => (
            <div key={i} style={s.fieldRow}>
              <input style={s.fieldLabel} placeholder="key" value={f.key}
                onChange={e => updateField(i, 'key', e.target.value)} />
              <input style={s.fieldInput} placeholder="Display Label" value={f.label}
                onChange={e => updateField(i, 'label', e.target.value)} />
              <input style={s.fieldInput} placeholder="Value" value={f.value}
                onChange={e => updateField(i, 'value', e.target.value)} />
              <button style={s.removeBtn} onClick={() => removeField(i)}>✕</button>
            </div>
          ))}

          <button style={s.addBtn} onClick={addField}>+ Add Field</button>
        </div>

        {/* ── Custom Letter Text (optional) ── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>✉️ Custom Letter Text (optional)</div>
          <label style={{ ...s.label, fontWeight: 400, color: '#888' }}>Opening paragraph — leave blank for default</label>
          <textarea style={s.textarea} placeholder="We are pleased to offer you the role of..."
            value={letterBody} onChange={e => setLetterBody(e.target.value)} />

          <label style={{ ...s.label, fontWeight: 400, color: '#888' }}>Closing paragraph — leave blank for default</label>
          <textarea style={{ ...s.textarea, minHeight: 60 }} placeholder="We look forward to having you on our team..."
            value={closingText} onChange={e => setClosingText(e.target.value)} />
        </div>

        {/* ── Save as Template ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
          <input style={{ ...s.fieldInput, maxWidth: 200 }} placeholder="Template name"
            value={saveTemplateName} onChange={e => setSaveTemplateName(e.target.value)} />
          <button style={s.addBtn} onClick={handleSaveTemplate}>💾 Save as Template</button>
        </div>

        {/* ── Expiry ── */}
        <label style={s.label}>Expiry</label>
        <select style={s.select} value={expiryHours} onChange={e => setExpiryHours(e.target.value)}>
          <option value={24}>24 hours</option>
          <option value={48}>48 hours</option>
          <option value={72}>72 hours (default)</option>
          <option value={168}>7 days</option>
          <option value={336}>14 days</option>
        </select>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...s.btn, ...(createMutation.isPending ? { opacity: 0.7 } : {}) }}
            onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : '🚀 Create & Generate QR'}
          </button>
          <button style={{ ...s.btn, background: '#6366f1' }} onClick={handlePreview}>
            👁 Preview
          </button>
        </div>

        {/* ── Result ── */}
        {result && (
          <div style={s.result}>
            <h3 style={{ fontWeight: 700, color: '#166534', marginBottom: 12 }}>✅ Offer Created</h3>
            <div style={s.qrWrap}><QRCodeSVG value={result.qrUrl} size={200} level="H" /></div>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>Offer URL:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ ...s.tokenBox, flex: 1, fontSize: 11, wordBreak: 'break-all' }}>{result.qrUrl}</div>
              <button
                onClick={() => { navigator.clipboard.writeText(result.qrUrl); toast.success('URL copied!'); }}
                style={{ padding: '8px 14px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                📋 Copy URL
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Token only (paste in mobile app):</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ ...s.tokenBox, flex: 1, fontSize: 10, wordBreak: 'break-all' }}>
                {result.qrUrl?.split('/offer/view/')[1]}
              </div>
              <button
                onClick={() => {
                  const token = result.qrUrl?.split('/offer/view/')[1];
                  navigator.clipboard.writeText(token);
                  toast.success('Token copied! Paste in mobile app.');
                }}
                style={{ padding: '8px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                📋 Copy Token
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#888', marginTop: 12 }}>Expires: {new Date(result.expiry).toLocaleString()}</p>
            <button
              style={{ ...s.btn, marginTop: 16, background: '#10b981', ...(notifyMutation.isPending ? { opacity: 0.7 } : {}) }}
              onClick={() => notifyMutation.mutate(result.offerId)}
              disabled={notifyMutation.isPending}>
              {notifyMutation.isPending ? 'Sending...' : '📧 Send Mail to Candidate'}
            </button>
          </div>
        )}
      </div>

      {/* ── Preview Modal ── */}
      {showPreview && (
        <div style={s.overlay} onClick={() => setShowPreview(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            {/* Company Header */}
            <div style={{ background: '#1a1a2e', padding: '24px 40px', display: 'flex', alignItems: 'center', gap: 16, borderRadius: '12px 12px 0 0' }}>
              <div style={{ width: 52, height: 52, borderRadius: 8, background: '#7c83fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🏢</div>
              <div>
                <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>{tenantName}</div>
                <div style={{ color: '#aaa', fontSize: 12 }}>Official Offer Letter — Preview</div>
              </div>
              <button style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#aaa', fontSize: 20, cursor: 'pointer' }} onClick={() => setShowPreview(false)}>✕</button>
            </div>

            {/* Letter body */}
            <div style={{ padding: '40px 56px', fontFamily: 'Georgia, serif', color: '#222', lineHeight: 1.8 }}>
              <p style={{ color: '#666', fontSize: 13, marginBottom: 24 }}>
                Date: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
              <p style={{ marginBottom: 4 }}>To,</p>
              <p style={{ fontWeight: 700, marginBottom: 24 }}>{selectedCandidate?.name || '[Candidate Name]'}</p>

              <p style={{ fontWeight: 700, fontSize: 15, borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 20 }}>
                Subject: Offer of Employment — {title || '[Offer Title]'}
              </p>

              <p style={{ marginBottom: 16 }}>Dear {selectedCandidate?.name || '[Candidate Name]'},</p>

              {/* Custom or default opening */}
              <p style={{ marginBottom: 16 }}>
                {letterBody || `We are delighted to extend this offer of employment to you for the position of ${fields.find(f => f.key === 'position')?.value || title} at ${tenantName}. After careful consideration, we believe that your skills and experience make you an excellent fit for our team.`}
              </p>

              {/* Terms table */}
              <p style={{ fontWeight: 700, color: '#7c83fd', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, marginTop: 24 }}>
                Terms of Employment
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 13 }}>
                <tbody>
                  {fields.filter(f => f.key.trim() && f.key !== 'letterBody' && f.key !== 'closingText').map((f, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 12px', background: '#f8f9fa', fontWeight: 600, width: '35%', color: '#555' }}>
                        {f.label || f.key}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#1a1a2e' }}>{f.value || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Custom or default closing */}
              <p style={{ marginBottom: 16 }}>
                {closingText || 'This offer is contingent upon the successful completion of background verification, submission of required documents, and signing of the employment agreement. We look forward to welcoming you to the team.'}
              </p>

              <p style={{ marginBottom: 24 }}>Please sign this offer letter below to indicate your acceptance.</p>

              <p style={{ marginBottom: 4 }}>Warm regards,</p>
              <p style={{ fontWeight: 700, marginBottom: 2 }}>{tenantName}</p>
              <p style={{ color: '#888', fontSize: 13 }}>Human Resources Department</p>

              {/* Signature placeholder */}
              <div style={{ marginTop: 24, padding: 16, border: '1px dashed #ddd', borderRadius: 8, background: '#fafafa' }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>✍️ Candidate will sign here</div>
                <div style={{ height: 60, background: '#fff', border: '1px dashed #ccc', borderRadius: 6 }} />
              </div>

              <p style={{ marginTop: 16, fontSize: 10, color: '#ccc', textAlign: 'center', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                Confidential · {selectedCandidate?.name || '[Candidate]'} · [IP Address] · [Timestamp]
              </p>
            </div>

            {/* Actions */}
            <div style={{ padding: '16px 40px 24px', display: 'flex', gap: 12, borderTop: '1px solid #eee' }}>
              <button style={{ ...s.btn, flex: 1 }} onClick={() => { setShowPreview(false); handleSubmit(); }}>
                ✅ Looks Good — Create & Send
              </button>
              <button style={s.cancelBtn} onClick={() => setShowPreview(false)}>✏️ Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
